/**
 * Record Input Values Policy (TV1-2453 slice 8)
 *
 * 🚨 ONE COMMAND. The seller's saved choices for a Sub-Goal's unfilled Declared
 * Inputs become `RecordSubGoalInputValues` on the AutomationRequest and NOTHING
 * ELSE. The server-side assignation policy writes them onto the approved rule. The
 * UI never sends `SetRule` or `SetRuleDeclaredInputsValues`.
 *
 * ── Read-after-write, honestly ──────────────────────────────────────────────
 *
 * After the command succeeds the policy re-reads the Sub-Goal's evidence ONCE, so
 * `canPromoteSubGoal` re-evaluates against the rule row. The values reach that row
 * asynchronously, through the server-side policy, so the re-read can still show the
 * input empty. The page then keeps the "falta completar" hint and the control, and
 * the next evidence read (reopening the request, or rows arriving) shows the filled
 * value. No optimistic update: the gate reads only what the rule row says.
 *
 * A refusal (for example `UNDECLARED_DECLARED_INPUT`) sets `ui.inputValuesError` for
 * the Sub-Goal and issues nothing else, not even the re-read.
 *
 * An empty `values` issues no command at all: the policy only clears the pending
 * request.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { RequestsState } from '../state';
import { RequestsCommand } from '../commands';
import { resolveShadowEvidence } from './shadowEvidencePolicy';

export const createRecordInputValuesPolicy = (
  backend: ClientLibraryType
): Policy<RequestsState, RequestsCommand> => {
  let closed = false;

  return {
    name: 'automation-requests-record-input-values',

    shouldReact: ( prev, next ) =>
      prev.ui.pendingInputValues === null && next.ui.pendingInputValues !== null,

    onStateChange: async ({ next, ctx }) => {
      const pending = next.ui.pendingInputValues;
      if ( !pending ) return;

      const subGoal = next.data.subGoals.find(( sg ) => sg.id === pending.subGoalId );

      try {
        if ( !subGoal ) return;
        // Nothing chosen: no command to send. The `finally` still clears pending.
        if ( Object.keys( pending.values ).length === 0 ) return;

        ctx.dispatch(
          { type: 'automation/requests/data/setInputValuesError', payload: null },
          { source: 'api' }
        );

        try {
          await backend.commands.RecordSubGoalInputValues({
            id: subGoal.requestId,
            body: { subGoalId: subGoal.id, values: pending.values }
          });
        } catch {
          if ( !closed ) {
            ctx.dispatch(
              { type: 'automation/requests/data/setInputValuesError', payload: { subGoalId: subGoal.id }},
              { source: 'api' }
            );
          }
          return;
        }

        const evidence = await resolveShadowEvidence( backend, subGoal );
        if ( !closed ) {
          ctx.dispatch(
            { type: 'automation/requests/data/setEvidence', payload: { subGoalId: subGoal.id, evidence }},
            { source: 'api' }
          );
        }
      } finally {
        if ( !closed ) {
          ctx.dispatch(
            {
              type: 'automation/requests/ui/clearPendingInputValues',
              payload: {} as Record<string, never>
            },
            { source: 'api' }
          );
        }
      }
    },

    cleanup: () => {
      closed = true;
    }
  };
};
